import React from 'react'
import PropTypes from 'prop-types'

/**
 * The Fieldset object handles vertical spacing in form fields so the spacing
 * between individual form fields is consistent and can be controlled via CSS.
 * @see https://bronson.vwfs.tools/default/docs/objects/fieldset.html
 *
 * @param {React.ReactNode|React.ReactNodeArray} children - Collection of {@link FieldsetRow} and other form child elements.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Fieldset element.
 * @constructor
 */
export function Fieldset({ children, testId, ...otherProps }) {
  return (
    <fieldset className="o-fieldset" data-testid={testId} {...otherProps}>
      {children}
    </fieldset>
  )
}

Fieldset.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]).isRequired,
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * Bronson FieldsetRow.
 * @internal
 *
 * @param {React.ReactNode|React.ReactNodeArray} children - Form-related child elements, like {@link FormField}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FieldsetRow element.
 * @constructor
 */
export function FieldsetRow({ children, testId, ...otherProps }) {
  return (
    <div className="o-fieldset__row" data-testid={testId} {...otherProps}>
      {children}
    </div>
  )
}

FieldsetRow.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]).isRequired,
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

FieldsetRow.displayName = 'Fieldset.Row'
Fieldset.Row = FieldsetRow
